import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable } from 'rxjs';
import { AdminSettings, AppTheme } from './admin-settings.model';
import { LocalStorageService } from './local-storage.service';
import { UserSession } from 'app/core/user-session';

const ADMIN_SETTINGS_KEY = 'adminSettings';
// Marks that a stored 2025 theme has been replaced by the blue-gold default, so a later deliberate
// pick of 2025 is not overwritten on the next load.
const BLUE_GOLD_DEFAULT_APPLIED_KEY = 'adminSettingsBlueGoldDefaultApplied';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private readonly destroyRef = inject(DestroyRef);
  initialSettings: AdminSettings = {
    adminLanguage: null,
    adminTheme: null,
    adminToggle: false,
    aiFeatures: false,
    showServerSideCampaigns: false,
    cookieFeatures: false,
    versionHistory: false,
    undoRedo: false,
    adminAccountLanguage: null,
    appTheme: AppTheme.Theme2026BlueGold,
  };
  private settingsSubject: BehaviorSubject<AdminSettings>;
  public settings$: Observable<AdminSettings>;
  public openAdminSettingsModal: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private nonAdmin: boolean;

  constructor(
    private localStorageService: LocalStorageService,
    private userSession: UserSession,
  ) {
    const storedSettings = this.localStorageService.getItem<AdminSettings>(ADMIN_SETTINGS_KEY);
    const initialValue = this.applyBlueGoldDefaultOnce(storedSettings);
    this.settingsSubject = new BehaviorSubject<AdminSettings>(initialValue);
    this.settings$ = this.settingsSubject.asObservable();

    this.userSession.onContextChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const currentContext = this.userSession.context;
      this.nonAdmin = currentContext && currentContext.isAdmin === false;
      if (this.nonAdmin) {
        const currentSettings = this.settingsSubject.value;
        const areSettingsEqual = (Object.keys(this.initialSettings) as Array<keyof AdminSettings>).every(
          (key) => currentSettings[key] === this.initialSettings[key],
        );
        if (!areSettingsEqual) {
          this.updateSettings(this.initialSettings, false);
        }
      }
    });
  }

  private applyBlueGoldDefaultOnce(stored: AdminSettings | null): AdminSettings {
    const pending = !this.localStorageService.getItem<boolean>(BLUE_GOLD_DEFAULT_APPLIED_KEY);
    if (pending) {
      // Marked even with nothing stored, so a 2025 theme picked before the next load is not
      // mistaken for a leftover and overwritten.
      this.localStorageService.setItem(BLUE_GOLD_DEFAULT_APPLIED_KEY, true);
    }
    if (!stored) {
      return this.initialSettings;
    }
    if (!pending || stored.appTheme !== AppTheme.Theme2025) {
      return stored;
    }
    const migrated = { ...stored, appTheme: this.initialSettings.appTheme };
    this.localStorageService.setItem(ADMIN_SETTINGS_KEY, migrated);
    return migrated;
  }

  updateSettings(newSettings: AdminSettings, shouldSave: boolean): void {
    this.settingsSubject.next(newSettings);
    if (shouldSave) {
      this.localStorageService.setItem(ADMIN_SETTINGS_KEY, newSettings);
    }
  }

  getCurrentSettings(): AdminSettings {
    return this.settingsSubject.getValue();
  }

  triggerOpenAdminSettingsModal() {
    this.openAdminSettingsModal.next(true);
  }
}
